import type { ReactNode } from "react";

/**
 * Simulated phone device frame that hosts every game screen.
 * Reproduces the DESK mockup device chrome: bezel, 9:41 status bar,
 * and the home indicator. Screen content renders inside `.phone-content`.
 * Pure presentation — no game logic, no testids the flow asserts on.
 */
export function PhoneFrame({ children }: { children: ReactNode }) {
  return (
    <div className="desk-stage">
      <aside className="desk-web-rail" data-testid="desk-web-rail">
        <div className="desk-web-brand">
          <span className="desk-web-logo-mark" aria-hidden="true">
            <span className="desk-web-logo-dot" />
          </span>
          <span className="desk-web-logo-word">DESK</span>
        </div>
        <p className="desk-web-tagline">
          Make it viral.
          <span className="desk-web-tagline-accent">Don&apos;t get played.</span>
        </p>
        <p className="desk-web-support">
          You&apos;re the new editor at DESK. Same footage, a hundred ways to
          spin it. Spot the spin, then decide how far you&apos;ll take it.
        </p>
        <p className="desk-web-hint" aria-hidden="true">
          Click, drag, and scroll to work the story.
        </p>

        {/* The loop, in the rail's dead space. A first-time visitor needs to
            know what the game IS before they press Continue. */}
        <ol className="desk-web-loop">
          <li>
            <span className="desk-web-loop-n">01</span>
            <span className="desk-web-loop-t">Cut the footage</span>
            <span className="desk-web-loop-d">
              Keep the beats that serve your angle.
            </span>
          </li>
          <li>
            <span className="desk-web-loop-n">02</span>
            <span className="desk-web-loop-t">Caption the story</span>
            <span className="desk-web-loop-d">
              The headline decides what people saw.
            </span>
          </li>
          <li>
            <span className="desk-web-loop-n">03</span>
            <span className="desk-web-loop-t">Push it live</span>
            <span className="desk-web-loop-d">
              Watch it land. Then face what you made.
            </span>
          </li>
        </ol>

        {/* Studio footer. Mirrors the one on starforge.rippleridge.games so
            every RippleRidge site carries the same way home and the same
            links. Desktop only - the rail is hidden on phones, where the
            route back lives in the intro footlinks and the About sheet. */}
        <footer className="desk-web-foot">
          <nav className="desk-web-foot-links" aria-label="RippleRidge elsewhere">
            <a
              href="https://discord.gg/5cgeCfj9Mg"
              target="_blank"
              rel="noreferrer noopener"
            >
              <svg viewBox="0 0 71 55" width="15" height="12" fill="currentColor" aria-hidden="true">
                <path d="M60.1 4.9A58.5 58.5 0 0045.4.2a.2.2 0 00-.2.1 40.8 40.8 0 00-1.8 3.7 54 54 0 00-16.2 0A37.4 37.4 0 0025.4.3a.2.2 0 00-.2-.1A58.4 58.4 0 0010.5 5a.2.2 0 00-.1.1A59.7 59.7 0 00.3 45.4a.2.2 0 00.1.2 58.8 58.8 0 0017.7 9 .2.2 0 00.3-.1 42 42 0 003.6-5.9.2.2 0 00-.1-.3 38.8 38.8 0 01-5.5-2.6.2.2 0 010-.4l1.1-.9a.2.2 0 01.2 0 42 42 0 0035.6 0 .2.2 0 01.2 0l1.1.9a.2.2 0 010 .3 36.4 36.4 0 01-5.5 2.7.2.2 0 00-.1.3 47.2 47.2 0 003.6 5.9.2.2 0 00.3.1A58.6 58.6 0 0070.2 45.6a.2.2 0 00.1-.2c1.2-12.4-.1-23.2-6.1-40.4a.2.2 0 00-.1-.1zM23.7 37.3c-3.5 0-6.4-3.2-6.4-7.1s2.8-7.1 6.4-7.1 6.5 3.2 6.4 7.1c0 3.9-2.8 7.1-6.4 7.1zm23.6 0c-3.5 0-6.4-3.2-6.4-7.1s2.8-7.1 6.4-7.1 6.5 3.2 6.4 7.1c0 3.9-2.8 7.1-6.4 7.1z" />
              </svg>
              Discord
            </a>
            <a
              href="https://x.com/RippleRidgeGame"
              target="_blank"
              rel="noreferrer noopener"
            >
              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              X
            </a>
            <a
              href="https://bsky.app/profile/rippleridgegames.bsky.social"
              target="_blank"
              rel="noreferrer noopener"
            >
              <svg viewBox="0 0 568 501" width="14" height="12" fill="currentColor" aria-hidden="true">
                <path d="M123.121 33.664C188.241 82.553 258.281 181.68 284 234.873c25.719-53.192 95.759-152.32 160.879-201.21C491.866-1.611 568-28.906 568 57.947c0 17.346-9.945 145.713-15.778 166.555-20.275 72.453-94.155 90.933-159.875 79.748C507.222 323.8 536.444 388.56 473.333 453.32c-119.86 122.992-172.272-30.859-185.702-70.281-2.462-7.227-3.614-10.608-3.631-7.733-.017-2.875-1.169.506-3.631 7.733-13.43 39.422-65.842 193.273-185.702 70.281-63.111-64.76-33.889-129.52 80.986-149.071-65.72 11.185-139.6-7.295-159.875-79.748C10.945 203.659 1 75.291 1 57.946 1-28.906 76.135-1.612 123.121 33.664z" />
              </svg>
              Bluesky
            </a>
            <a href="mailto:rippleridgegames@gmail.com">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
                <path d="M3 6.5l9 6 9-6" />
              </svg>
              Email
            </a>
          </nav>
          <p className="desk-web-foot-note">
            Also from the studio:{" "}
            <a
              href="https://starforge.rippleridge.games"
              target="_blank"
              rel="noreferrer noopener"
            >
              Star Forge
            </a>
            <span className="desk-web-foot-copy">
              ©&nbsp;2026{" "}
              <a
                className="desk-web-foot-home"
                href="https://rippleridge.games"
                target="_blank"
                rel="noreferrer noopener"
              >
                <img
                  src="/assets/brand/rippleridge-logo.png"
                  alt=""
                  width={22}
                  height={14}
                  loading="lazy"
                />
                RippleRidge
              </a>
            </span>
          </p>
        </footer>
      </aside>
      <div className="phone-device">
        <div className="phone-screen">
          <div className="phone-statusbar" aria-hidden="true">
            <span className="phone-clock">9:41</span>
            <div className="phone-status-right">
              <span>5G</span>
              <div className="phone-battery">
                <div className="phone-battery-fill" />
              </div>
            </div>
          </div>
          <div className="phone-content">{children}</div>
          <div className="phone-home-indicator" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}
